/** Static failure identifiers only; never retain rejected values or raw exception messages. */
export type InsightsFailureClass =
  | 'network'
  | 'timeout'
  | 'http'
  | 'json_decode'
  | 'render_contract'
  | 'unsupported_presentation'
  | 'scope'
  | 'cancelled'
export type RenderIssue = {
  failureClass: 'render_contract' | 'unsupported_presentation'
  issueCode: string
  issuePath: string
}
export class InsightsFailure extends Error {
  constructor(
    readonly failureClass: InsightsFailureClass,
    readonly issueCode: string,
    readonly issuePath: string,
  ) {
    super(`Insights: ${failureClass}`)
    this.name = 'InsightsFailure'
  }
}
// Only schema-authored keys are eligible. Customer labels and unknown discriminants never enter paths.
const keys = new Set([
  'state',
  'context',
  'shop',
  'orgId',
  'connectionId',
  'revision',
  'answers',
  'evidence',
  'composition',
  'page',
  'sections',
  'id',
  'kind',
  'presentation',
  'title',
  'help',
  'why',
  'how',
  'notes',
  'code',
  'text',
  'sources',
  'source',
  'syncedAt',
  'start',
  'end',
  'appliesTo',
  'sourceRef',
  'baseRef',
  'measurement',
  'categoryKey',
  'bandKey',
  'field',
  'cards',
  'datum',
  'label',
  'unit',
  'result',
  'value',
  'currencyCode',
  'reason',
  'message',
  'values',
  'emphasis',
  'relationship',
  'base',
  'steps',
  'bars',
  'scale',
  'min',
  'max',
  'expansion',
  'showLabel',
  'hideLabel',
  'totalRows',
  'remainingRows',
  'note',
  'items',
  'description',
  'link',
  'targetId',
  'footer',
  'icon',
  'panels',
  'metric',
  'caption',
  'rows',
  'layout',
])
export function staticIssuePath(path: readonly PropertyKey[]) {
  return (
    path
      .map((key) => {
        if (typeof key === 'number') return '[]'
        if (keys.has(String(key))) return String(key)
        return 'unknown'
      })
      .join('.')
      .slice(0, 100) || 'root'
  )
}
export function renderIssue(
  error: { issues: readonly { code: string; path: readonly PropertyKey[] }[] },
  prefix: readonly PropertyKey[] = [],
): RenderIssue {
  const issue = error.issues[0]
  return {
    failureClass: 'render_contract',
    issueCode: issue?.code ?? 'invalid_shape',
    issuePath: staticIssuePath([...prefix, ...(issue?.path ?? [])]),
  }
}
export function throwRenderIssue(error: Parameters<typeof renderIssue>[0], prefix: readonly PropertyKey[] = []): never {
  const issue = renderIssue(error, prefix)
  throw new InsightsFailure(issue.failureClass, issue.issueCode, issue.issuePath)
}
